import { createFileRoute } from "@tanstack/react-router";

// Daily views refresh, driven by a scheduled job (pg_cron every 2 min during
// the 04:00 Europe/Madrid window). Each call processes up to `limit` videos
// (default 500, max 1000) that have no view snapshot for today yet, across
// ALL channels (including hidden ones). DB writes are parallelised in
// sub-batches of 50 to keep each call fast.
export const Route = createFileRoute("/api/public/hooks/refresh-views")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      POST: async ({ request }) => {
        const provided =
          request.headers.get("x-cron-secret") ??
          request.headers.get("authorization")?.replace(/^Bearer\s+/i, "") ??
          "";
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: tokenRow } = await supabaseAdmin
          .from("cron_tokens")
          .select("token")
          .eq("name", "refresh-views")
          .maybeSingle();
        const expected = tokenRow?.token ?? process.env["CRON_SECRET"];
        if (!expected || !provided || provided !== expected) {
          return json({ error: "Unauthorized" }, 401);
        }

        // Only run inside the intended local window (04:00 in Spain), so the
        // schedule stays correct across daylight-saving changes.
        const enforceWindow = new URL(request.url).searchParams.get("force") !== "1";
        const madridHour = Number(
          new Intl.DateTimeFormat("en-GB", {
            timeZone: "Europe/Madrid",
            hour: "2-digit",
            hour12: false,
          }).format(new Date()),
        );
        if (enforceWindow && madridHour !== 4) {
          return json({ skipped: true, reason: `local hour ${madridHour} != 4` });
        }

        const limit = Math.min(
          Math.max(Number(new URL(request.url).searchParams.get("limit") ?? 500), 1),
          1000,
        );

        const today = new Date().toISOString().slice(0, 10);

        // Snapshots already stored today (paged: PostgREST caps rows per request).
        const doneIds = new Set<string>();
        for (let from = 0; ; from += 1000) {
          const { data, error } = await supabaseAdmin
            .from("video_view_history")
            .select("video_id")
            .eq("recorded_date", today)
            .order("video_id", { ascending: true })
            .range(from, from + 999);
          if (error) return json({ error: error.message }, 500);
          for (const r of data ?? []) doneIds.add(r.video_id);
          if (!data || data.length < 1000) break;
        }

        // Walk videos in pages until we gather a batch that still needs a snapshot.
        const pending: {
          id: string;
          youtube_video_id: string;
          title: string | null;
          title_original: string | null;
          youtuber_id: string | null;
        }[] = [];
        let remaining = 0;
        for (let from = 0; ; from += 1000) {
          const { data, error } = await supabaseAdmin
            .from("videos")
            .select("id, youtube_video_id, title, title_original, youtuber_id")
            .order("id", { ascending: true })
            .range(from, from + 999);
          if (error) return json({ error: error.message }, 500);
          for (const v of data ?? []) {
            if (doneIds.has(v.id)) continue;
            if (pending.length < limit) pending.push(v);
            else remaining++;
          }
          if (!data || data.length < 1000) break;
        }

        const batch = pending;
        if (batch.length === 0) {
          return json({ done: true, remaining: 0, updated: 0 });
        }

        const { fetchVideoStats } = await import("@/lib/youtube.server");
        const stats = await fetchVideoStats(batch.map((v) => v.youtube_video_id));
        const statsMap = new Map(stats.map((s) => [s.videoId, s]));

        let updated = 0;
        let titlesChanged = 0;
        const touchedChannels = new Set<string>();
        const nowIso = new Date().toISOString();

        // Process DB writes in parallel sub-batches of 50.
        // YouTube stats are already fetched above in one call (fetchVideoStats
        // chunks internally at 50 per API request), so this only affects DB I/O.
        const WRITE_BATCH = 50;
        for (let i = 0; i < batch.length; i += WRITE_BATCH) {
          const sub = batch.slice(i, i + WRITE_BATCH);
          const historyRows: {
            video_id: string;
            view_count: number;
            like_count: number;
            comment_count: number;
            recorded_date: string;
            recorded_at: string;
          }[] = [];

          await Promise.all(
            sub.map(async (v) => {
              const s = statsMap.get(v.youtube_video_id);
              if (!s) return;
              const patch: {
                view_count: number;
                like_count: number;
                comment_count: number;
                title?: string;
                title_original?: string;
              } = {
                view_count: s.viewCount,
                like_count: s.likeCount,
                comment_count: s.commentCount,
              };
              if (s.title && v.title_original) {
                // Locally translated title: keep it, only track the original.
                if (s.title !== v.title_original) patch["title_original"] = s.title;
              } else if (s.title && s.title !== v.title) {
                patch["title"] = s.title;
                titlesChanged++;
              }
              await supabaseAdmin.from("videos").update(patch).eq("id", v.id);
              historyRows.push({
                video_id: v.id,
                view_count: s.viewCount,
                like_count: s.likeCount,
                comment_count: s.commentCount,
                recorded_date: today,
                recorded_at: nowIso,
              });
              if (v.youtuber_id) touchedChannels.add(v.youtuber_id);
              updated++;
            }),
          );

          // Batch-upsert all history rows for this sub-batch in one call.
          if (historyRows.length > 0) {
            await supabaseAdmin
              .from("video_view_history")
              .upsert(historyRows, { onConflict: "video_id,recorded_date" });
          }
        }

        await Promise.all(
          [...touchedChannels].map((id) =>
            supabaseAdmin
              .from("youtubers")
              .update({ last_views_refresh_at: nowIso })
              .eq("id", id),
          ),
        );

        return json({ done: remaining === 0, updated, titlesChanged, remaining });
      },
    },
  },
});

function json(body: unknown, status = 200) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}
